<template>
    <div
        v-if="isVisible"
        data-testid="checkout-darts-modal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
    >
        <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <h2
                    class="font-title mb-6 text-center text-base font-bold tracking-wide text-white uppercase"
                >
                    {{ $t('checkout_darts.darts_on_double') }}
                </h2>

                <div
                    class="mb-6 grid gap-4"
                    :class="{
                        'grid-cols-3': availableDoubleAttempts.length === 3,
                        'grid-cols-2': availableDoubleAttempts.length === 2,
                        'grid-cols-1': availableDoubleAttempts.length === 1,
                    }"
                >
                    <button
                        v-for="count in availableDoubleAttempts"
                        :key="'double-' + count"
                        @click="selectDoubleAttempts(count)"
                        class="flex h-12 items-center justify-center border text-white transition-colors"
                        :class="
                            selectedDoubleAttempts === count
                                ? 'border-red-600 bg-red-600'
                                : 'border-stroke-1 hover:border-red-600'
                        "
                        :disabled="isSubmitting"
                    >
                        <div class="text-base">{{ count }}</div>
                    </button>
                </div>

                <h2
                    class="font-title mb-6 text-center text-base font-bold tracking-wide text-white uppercase"
                >
                    {{ $t('checkout_darts.darts_for_checkout') }}
                </h2>

                <div
                    class="grid gap-4"
                    :class="{
                        'grid-cols-3': availableDartCounts.length === 3,
                        'grid-cols-2': availableDartCounts.length === 2,
                        'grid-cols-1': availableDartCounts.length === 1,
                    }"
                >
                    <button
                        v-for="dartCount in availableDartCounts"
                        :key="'checkout-' + dartCount"
                        @click="selectDartCount(dartCount)"
                        class="flex h-12 items-center justify-center border text-white transition-colors"
                        :class="
                            selectedDartCount === dartCount
                                ? 'border-red-600 bg-red-600'
                                : 'border-stroke-1 hover:border-red-600'
                        "
                        :disabled="isSubmitting"
                    >
                        <div class="text-base">{{ dartCount }}</div>
                    </button>
                </div>
            </div>

            <div class="border-stroke-1 mt-2 flex gap-2 border-t p-4">
                <DiagonalCutButton
                    @click="onCancel"
                    variant="secondary"
                    cut-position="right-top"
                    :disabled="isSubmitting"
                    class="flex-1"
                >
                    {{ $t('checkout_darts.cancel') }}
                </DiagonalCutButton>
                <DiagonalCutButton
                    @click="onConfirm"
                    variant="primary"
                    cut-position="left-top"
                    :disabled="isSubmitting || !isValid"
                    class="flex-1"
                    data-testid="checkout-darts-confirm"
                >
                    {{ $t('checkout_darts.confirm') }}
                </DiagonalCutButton>
            </div>
        </div>
    </div>
</template>

<script>
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'

export default {
    name: 'CheckoutDartsModal',
    components: { DiagonalCutButton },
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
        availableDartCounts: {
            type: Array,
            default: () => [1, 2, 3],
        },
        availableDoubleAttempts: {
            type: Array,
            default: () => [1, 2, 3],
        },
        isSubmitting: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select', 'cancel'],
    data() {
        return {
            selectedDoubleAttempts: 1,
            selectedDartCount: null,
        }
    },
    computed: {
        isValid() {
            return this.selectedDartCount !== null && this.selectedDoubleAttempts !== null
        },
    },
    watch: {
        isVisible(newVal) {
            if (newVal) {
                this.selectedDoubleAttempts =
                    this.availableDoubleAttempts.length > 0 ? this.availableDoubleAttempts[0] : 1
                this.selectedDartCount =
                    this.availableDartCounts.length > 0 ? this.availableDartCounts[0] : null
            }
        },
        availableDartCounts: {
            immediate: true,
            handler(newCounts) {
                if (newCounts.length > 0 && this.selectedDartCount === null) {
                    this.selectedDartCount = newCounts[0]
                }
            },
        },
    },
    methods: {
        selectDoubleAttempts(count) {
            if (this.isSubmitting) return
            this.selectedDoubleAttempts = count

            if (this.selectedDartCount !== null && count > this.selectedDartCount) {
                this.selectedDartCount = count
            }
        },
        selectDartCount(dartCount) {
            if (this.isSubmitting) return
            this.selectedDartCount = dartCount

            if (this.selectedDoubleAttempts > dartCount) {
                this.selectedDoubleAttempts = dartCount
            }
        },
        onConfirm() {
            if (!this.isSubmitting && this.isValid) {
                this.$emit('select', {
                    dartCount: this.selectedDartCount,
                    doubleAttempts: this.selectedDoubleAttempts,
                })
            }
        },
        onCancel() {
            if (!this.isSubmitting) {
                this.$emit('cancel')
            }
        },
    },
}
</script>
